import {Action, State, StateContext, Store} from '@ngxs/store';
import {itemsPerPage} from '@shared/helpers';
import {Injectable} from '@angular/core';
import {DeviceService} from '@services/device/device.service';
import {CookieService} from 'ngx-cookie';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, map, mergeMap, tap} from 'rxjs/operators';
import {IResponse} from '@interfaces/response';
import {IDevice, IDeviceInfo, IUnapprovedDevice} from '@interfaces/devices';
import {DeviceOrientation} from '@shared/enums/device-orientation.enum';
import {DevicesStateModel} from './devices-state.model';
import {DeviceProofMode} from '@shared/device-proof';
import {
  AddDevicesToGroup,
  ChangeCurrentDevicesPage, ChangeDevicePageWithoutUrlParams, ChangeDeviceSearchParam,
  ChangeDevicesMenuStatus,
  ChangeDevicesPerPage,
  CleanCurrentDevicesPageParam, CleanDevices,
  CleanSearchDevicesParam, CleanSelectedDevices,
  CleanStoredDevices,
  CreateDevice,
  DeleteDevice,
  DeleteDevicesFromGroup,
  EditDevice, UpdateDeviceProofMode,
  FetchDevices,
  GetAmountOfAllSystemDevices,
  GetCSVReport,
  GetDevicesInfo, GetDevicesWithoutGroupTotal,
  GetScreenshot, GetScreenshotV2,
  GetUnapprovedDevices, GetUngroupedDevices,
  RestartDevice,
  SearchDevices,
  SetCurrentDevicesPage, SetDefaultSortParam,
  SetDeviceDefaultPerPage,
  SetDevicesPerPage,
  SetSearchDevicesParam,
  SetSelectedDevice, SortDevices,
  ToggleDeviceInfo,
} from './devices.actions';
import {FetchGroups, GroupsSelectors} from '../groups';
import {Navigate} from '@ngxs/router-plugin';
import {GroupService} from '@services/group/group.service';
import {IGroup} from '@interfaces/group';
import {ToggleDeviceLoader, ToggleGlobalLoader, ToggleUnapprovedLoader, ToggleUngroupedDevicesLoader} from '@store/loader';
import {normalizeScreenshot} from '@shared/functions';

@State<DevicesStateModel>({
  name: 'devicesState',
  defaults: {
    allSystemDevices: 0,
    devicesPerPage: itemsPerPage[5],
    devices: null,
    selectedDevice: null,
    devicesInfo: [],
    unapprovedDevices: null,
    unapprovedTotal: 0,
    devicesWithoutGroup: null,
    devicesWithoutGroupTotal: 0,
    mobileDevicesMenuStatus: false,
    currentPage: 1,
    searchParam: '',
    screenshot: null,
    screenshotLoading: false,
    sortParam: '-id',
  }
})
@Injectable()
export class DevicesState {
  private proofRevision = 0;
  private proofUpdates = new Map<string, {revision: number; mode: DeviceProofMode}>();

  private preserveUpdatedProof(device: IDevice, requestRevision: number): IDevice {
    const update = this.proofUpdates.get(device.guid);
    return update && update.revision > requestRevision ? {...device, proofMode: update.mode} : device;
  }

  constructor(
    private deviceService: DeviceService,
    private groupService: GroupService,
    private cookie: CookieService,
    private store: Store,
  ) {
  }

  @Action(UpdateDeviceProofMode)
  updateProofMode(ctx: StateContext<DevicesStateModel>, {guid, mode}: UpdateDeviceProofMode): void {
    this.proofUpdates.set(guid, {revision: ++this.proofRevision, mode});
    const state = ctx.getState();
    const update = (device: IDevice): IDevice => device.guid === guid ? {...device, proofMode: mode} : device;
    ctx.patchState({
      selectedDevice: state.selectedDevice ? update(state.selectedDevice) : null,
      devices: state.devices ? {...state.devices, rows: state.devices.rows.map(update)} : null,
      devicesWithoutGroup: state.devicesWithoutGroup
        ? {...state.devicesWithoutGroup, rows: state.devicesWithoutGroup.rows.map(update)} : null
    });
  }

  // ACTIONS
  @Action(FetchDevices, {cancelUncompleted: true})
  fetchDevices(ctx: StateContext<DevicesStateModel>, {params}: FetchDevices): Observable<any> {
    const proofRevision = this.proofRevision;
    const limit = ctx.getState().devicesPerPage;
    const skip = limit * (ctx.getState().currentPage - 1);
    const search = ctx.getState().searchParam;
    const selectedGroup = this.store.selectSnapshot(GroupsSelectors.getSelectedGroup);
    const selectedGroupTimezone = selectedGroup?.timezone;
    const order = params?.order || ctx.getState().sortParam;

    this.store.dispatch(new ToggleDeviceLoader(true));

    const request = selectedGroup ?
      this.groupService.getDevicesByGroupId({
        groupId: selectedGroup.id, skip, order, limit, search
      }) :
      this.deviceService.getDevices({
        limit,
        order,
        skip,
        search,
        ...params
      });

    return request
      .pipe(
        map(devices => ({...devices, rows: devices.rows.map(device => this.preserveUpdatedProof(device, proofRevision))})),
        map((devices: IResponse<IDevice>) => {
          if (!selectedGroupTimezone) return devices;

          return {
            ...devices,
            rows: devices.rows.map((device) => ({
              ...device,
              groupTimezone: device.groupTimezone || selectedGroupTimezone,
            })),
          };
        }),
        tap((devices: IResponse<IDevice>) => {
          const state = ctx.getState();
          ctx.setState({...state, devices});
          this.store.dispatch(new ToggleDeviceLoader(false));
        }),
      );
  }

  @Action(ChangeDevicesPerPage)
  changeDevicesPerPage(ctx: StateContext<DevicesStateModel>, {devicesPerPage}: ChangeDevicesPerPage): Observable<void> {
    this.setDevicesPerPage(ctx, {devicesPerPage});
    return ctx.dispatch(new FetchDevices());
  }

  @Action(SetDevicesPerPage)
  setDevicesPerPage(ctx: StateContext<DevicesStateModel>, {devicesPerPage}: ChangeDevicesPerPage): DevicesStateModel {
    this.cookie.put('devicesPerPage', devicesPerPage.toString());
    ctx.patchState({currentPage: 1});

    return ctx.patchState({devicesPerPage});
  }

  @Action(ChangeDevicesMenuStatus)
  changeDevicesMenuStatus(ctx: StateContext<DevicesStateModel>, {status}: ChangeDevicesMenuStatus): void {
    ctx.patchState({mobileDevicesMenuStatus: status});
  }

  @Action(GetAmountOfAllSystemDevices)
  getAllSystemDevices(ctx: StateContext<DevicesStateModel>): Observable<IResponse<IDevice>> {
    return this.deviceService.getDevices({limit: 10})
      .pipe(
        tap((res: IResponse<IDevice>) => {
          ctx.patchState({allSystemDevices: res.total});
        })
      );
  }

  @Action(SetCurrentDevicesPage)
  setCurrentDevicesPage(ctx: StateContext<DevicesStateModel>, {currentPage}: ChangeCurrentDevicesPage): Observable<any> {
    ctx.patchState({currentPage});

    return ctx.dispatch(
      new Navigate([], {devicesPage: currentPage}, {queryParamsHandling: 'merge'}),
    );
  }

  @Action(ChangeCurrentDevicesPage)
  changeCurrentDevicesPage(ctx: StateContext<DevicesStateModel>, {currentPage}: ChangeCurrentDevicesPage): Observable<any> {
    this.setCurrentDevicesPage(ctx, {currentPage});

    return ctx.dispatch(new FetchDevices());
  }

  @Action(SearchDevices)
  searchDevices(ctx: StateContext<DevicesStateModel>, {searchParam}: SearchDevices): Observable<void> {
    this.setSearchDevicesParam(ctx, {searchParam});

    return ctx.dispatch([
      new FetchDevices()
    ]);
  }

  @Action(SetSearchDevicesParam)
  setSearchDevicesParam(ctx: StateContext<DevicesStateModel>, {searchParam}: SetSearchDevicesParam): Observable<void> {
    ctx.patchState({searchParam, currentPage: 1});
    const navigate = searchParam ?
      new Navigate([], {devicesSearch: searchParam, devicesPage: 1}, {queryParamsHandling: 'merge'}) :
      new Navigate([], {devicesSearch: null, devicesPage: 1}, {queryParamsHandling: 'merge'});

    return ctx.dispatch(navigate);
  }

  // Created for avoid issue with ngxs router, ngxs is canceled routing through router link
  @Action(CleanSearchDevicesParam)
  cleanSearchDevices(ctx: StateContext<DevicesStateModel>): DevicesStateModel {
    return ctx.patchState({searchParam: ''});
  }

  // Created for avoid issue with ngxs router, ngxs is canceled routing through router link
  @Action(CleanCurrentDevicesPageParam)
  cleanCurrentDevicesPageParam(ctx: StateContext<DevicesStateModel>): DevicesStateModel {
    return ctx.patchState({currentPage: 1});
  }

  @Action(DeleteDevice, {cancelUncompleted: true})
  deleteDevice(ctx: StateContext<DevicesStateModel>, {id}: DeleteDevice): Observable<void> {
    return this.deviceService.remove(id)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const currentPage = this.getCurrentPageAfterDelete(state.currentPage, state.devicesPerPage, state.devices?.total, 1);

          ctx.patchState({
            allSystemDevices: Math.max(state.allSystemDevices - 1, 0),
            currentPage
          });

          this.store.dispatch(new Navigate([], {devicesPage: currentPage}, {queryParamsHandling: 'merge'}));
        }),
        mergeMap(() => ctx.dispatch(new FetchDevices()))
      );
  }

  @Action(DeleteDevicesFromGroup, {cancelUncompleted: true})
  deleteDevicesFromGroup(ctx: StateContext<DevicesStateModel>, {deviceIds, groupId}: DeleteDevicesFromGroup): Observable<void> | undefined {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.deviceService.removeDeviceFromGroup(groupId, deviceIds)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const currentPage = this.getCurrentPageAfterDelete(
            state.currentPage,
            state.devicesPerPage,
            state.devices?.total,
            deviceIds.length
          );

          ctx.patchState({currentPage});
          this.store.dispatch(new Navigate([], {devicesPage: currentPage}, {queryParamsHandling: 'merge'}));
        }),
        mergeMap(() => ctx.dispatch([new FetchDevices(), new FetchGroups(), new ToggleGlobalLoader(false)]))
      );
  }

  @Action(CreateDevice, {cancelUncompleted: true})
  createDevice(ctx: StateContext<DevicesStateModel>, {device}: CreateDevice): Observable<void> {
    const deviceWithTemplate = {...device, template: device.orientation === DeviceOrientation.landscape ? 1 : 2};
    return this.deviceService.createDevice(deviceWithTemplate)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          ctx.patchState({allSystemDevices: state.allSystemDevices + 1});
        }),
        mergeMap(() => ctx.dispatch([
          new FetchDevices(),
          new GetDevicesWithoutGroupTotal(),
          new FetchGroups()
        ]))
      );
  }

  @Action(EditDevice, {cancelUncompleted: true})
  editDevice(ctx: StateContext<DevicesStateModel>, {device}: EditDevice): Observable<void> {
    return this.deviceService.editDevice(device)
      .pipe(
        mergeMap(() => ctx.dispatch(new FetchDevices()))
      );
  }

  @Action(GetDevicesInfo, {cancelUncompleted: true})
  getDevicesInfo(ctx: StateContext<DevicesStateModel>, {guids}: GetDevicesInfo): Observable<{ [key: string]: IDeviceInfo }[] | null> {
    if (!guids.length) return of(null);
    return this.deviceService.getDevicesInfo(guids)
      .pipe(
        tap((info) => ctx.setState({...ctx.getState(), devicesInfo: info}))
      );
  }

  @Action(GetUnapprovedDevices, {cancelUncompleted: true})
  getUnapprovedDevices(ctx: StateContext<DevicesStateModel>): Observable<IResponse<IUnapprovedDevice>> {
    this.store.dispatch(new ToggleUnapprovedLoader(true));
    return this.deviceService.getUnapprovedDevices()
      .pipe(
        tap((devices) => {
          ctx.setState({...ctx.getState(), unapprovedDevices: devices, unapprovedTotal: devices.total});
          this.store.dispatch(new ToggleUnapprovedLoader(false));
        })
      );
  }

  @Action(GetUngroupedDevices, {cancelUncompleted: true})
  getDevicesWithoutGroup(ctx: StateContext<DevicesStateModel>, {params}: GetUngroupedDevices): Observable<IResponse<IDevice>> {
    const proofRevision = this.proofRevision;
    this.store.dispatch(new ToggleUngroupedDevicesLoader(true));
    return this.deviceService.getDevices({
      noGroup: 1,
      ...params
    }).pipe(
        map(response => ({...response, rows: response.rows.map(device => this.preserveUpdatedProof(device, proofRevision))})),
        tap((response) => {
          const state = ctx.getState();
          ctx.setState({
            ...state,
            devicesWithoutGroup: {...response, rows: response.rows},
            devicesWithoutGroupTotal: response.total
          });
          this.store.dispatch(new ToggleUngroupedDevicesLoader(false));
        })
    );
  }

  @Action(GetDevicesWithoutGroupTotal, {cancelUncompleted: true})
  getDevicesWithoutGroupTotal(ctx: StateContext<DevicesStateModel>): Observable<IResponse<IDevice>> {
    return this.deviceService.getDevices({
      noGroup: 1,
      limit: 1
    }).pipe(
        tap((response) => {
          const state = ctx.getState();
          ctx.setState({
            ...state,
            devicesWithoutGroupTotal: response.total
          });
        })
    );
  }

  @Action(SetSelectedDevice)
  setSelectedDevice(ctx: StateContext<DevicesStateModel>, {id}: SetSelectedDevice): Observable<IDevice> {
    const proofRevision = this.proofRevision;
    return this.deviceService.getSingleDeviceInfo(id)
      .pipe(
        map(device => this.preserveUpdatedProof(device, proofRevision)),
        tap((device) => {
          ctx.setState({...ctx.getState(), selectedDevice: device, screenshot: null});
        })
      );
  }

  @Action(GetScreenshot, { cancelUncompleted: true })
  getScreenshot(ctx: StateContext<DevicesStateModel>, { deviceGuid }: GetScreenshot): Observable<{ date: string; scr: string }> {
    ctx.patchState({ screenshotLoading: true });

    return this.deviceService.getScreenshot(deviceGuid).pipe(
        map((screenshot) => normalizeScreenshot(screenshot)),
        tap((screenshot) => {
          ctx.patchState({ screenshot });
        }),
        finalize(() => {
          ctx.patchState({ screenshotLoading: false });
        })
    );
  }

  @Action(GetScreenshotV2, { cancelUncompleted: true })
  getScreenshotV2(ctx: StateContext<DevicesStateModel>, { deviceGuid }: GetScreenshot): Observable<{ date: string; scr: string }> {
    ctx.patchState({ screenshotLoading: true });

    return this.deviceService.getScreenshotV2(deviceGuid).pipe(
        map((screenshot) => normalizeScreenshot(screenshot)),
        tap((screenshot) => {
          ctx.patchState({ screenshot });
        }),
        finalize(() => {
          ctx.patchState({ screenshotLoading: false });
        })
    );
  }

  @Action(CleanStoredDevices)
  cleanStoredDevices(ctx: StateContext<DevicesStateModel>): void {
    ctx.patchState({devices: null});
  }

  @Action(GetCSVReport)
  getCSVReport(ctx: StateContext<DevicesStateModel>, {devices}: GetCSVReport): Observable<IDevice[]> | void {
    if (!devices?.length) return;

    return this.deviceService.getDevicesInfo(devices.map((device) => device.guid))
      .pipe(
        mergeMap((info) => {
          const devicesWithInfo = this.deviceService.mergeDevicesWithInfo(devices, info || []);
          const onlineDeviceGuids = new Set(devicesWithInfo
            .filter((device) => Boolean(device.info) && !device.info?.lastConnectTime && !device.info?.lastDisconnectTime)
            .map((device) => device.guid));

          return forkJoin(devicesWithInfo.map((device) => {
            if (device.info?.lastConnectTime || device.info?.lastDisconnectTime) return of(device);

            return this.deviceService.getDeviceHistory(device.input_id)
              .pipe(
                map((history) => history ? ({
                  ...device,
                  info: {...(device.info || {}), ...history} as IDeviceInfo
                }) : device),
                catchError(() => of(device))
              );
          })).pipe(
            tap((devicesWithHistory) => {
              this.deviceService.downloadCSV(devicesWithHistory, onlineDeviceGuids);
            })
          );
        }),
      );
  }

  @Action(AddDevicesToGroup, {cancelUncompleted: true})
  addDevicesToGroup(ctx: StateContext<DevicesStateModel>, {groupId, devicesId}: AddDevicesToGroup): Observable<IGroup[]> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.deviceService.addDevicesToGroup(groupId, devicesId)
      .pipe(
        tap(() => ctx.dispatch([
            new FetchDevices(),
            new FetchGroups(),
            new GetDevicesWithoutGroupTotal(),
            new ToggleGlobalLoader(false)
        ]))
      );
  }

  @Action(ToggleDeviceInfo)
  toggleDeviceInfo(ctx: StateContext<DevicesStateModel>, {guid, status}: ToggleDeviceInfo): void {
    this.deviceService.deviceInfoController(guid, status);
  }

  @Action(RestartDevice)
  restartDevice(ctx: StateContext<DevicesStateModel>, {guid}: RestartDevice): void {
    this.deviceService.restart(guid);
  }

  @Action(SetDeviceDefaultPerPage)
  setDeviceDefaultPerPage(ctx: StateContext<DevicesStateModel>): void {
    ctx.patchState({devicesPerPage: itemsPerPage[5], currentPage: 1});
  }

  @Action(ChangeDevicePageWithoutUrlParams)
  changeDevicePageWithoutUrlParams(ctx: StateContext<DevicesStateModel>, {currentPage}: ChangeDevicePageWithoutUrlParams): void {
    ctx.patchState({currentPage});
  }

  @Action(ChangeDeviceSearchParam)
  changeDeviceSearchParam(ctx: StateContext<DevicesStateModel>, {searchParam}: ChangeDeviceSearchParam): void {
    ctx.patchState({searchParam, currentPage: 1});
  }

  @Action(CleanDevices)
  cleanDevices(ctx: StateContext<DevicesStateModel>): void {
    ctx.patchState({currentPage: 1, devices: null, searchParam: ''});
  }

  @Action(CleanSelectedDevices)
  cleanSelectedDevices(ctx: StateContext<DevicesStateModel>): void {
    ctx.patchState({selectedDevice: null});
  }

  @Action(SortDevices)
  sortDevices(ctx: StateContext<DevicesStateModel>, {param}: SortDevices): void {
    ctx.patchState({sortParam: param});
    ctx.dispatch(new FetchDevices());
  }

  @Action(SetDefaultSortParam)
  setDefaultSortParam(ctx: StateContext<DevicesStateModel>): void {
    ctx.patchState({sortParam: '-id'});
  }

  private getCurrentPageAfterDelete(currentPage: number, perPage: number, total = 0, deletedCount = 1): number {
    const totalAfterDelete = Math.max(total - deletedCount, 0);
    const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / perPage), 1);

    return Math.min(currentPage, lastAvailablePage);
  }
}
